/**
 * Connection manager for the remote web client.
 * Manages WsRpcClient lifecycle, localStorage persistence, and visibility-based reconnection.
 */

import { WsRpcClient, type WsRpcClientOptions, type TransportConnectionState } from './client'
import { buildClientApi } from './build-api'
import { CHANNEL_MAP } from './channel-map'

const STORAGE_KEY = 'origin-relay-connection'
const HISTORY_KEY = 'origin-relay-connection-history'
const MAX_HISTORY = 10

interface StoredConnection {
  host: string
  port: number
  workspaceId?: string
  protocol?: 'ws' | 'wss'
}

export interface ConnectionHistoryEntry {
  host: string
  port: number
  protocol: 'ws' | 'wss'
  label: string
  lastConnected: number
}

let client: WsRpcClient | null = null
let currentConnection: StoredConnection | null = null
let visibilityHandler: (() => void) | null = null

export function getStoredConnection(): StoredConnection | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as StoredConnection
  } catch {
    return null
  }
}

export function storeConnection(conn: StoredConnection): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(conn))
  } catch {
    // Safari private browsing may fail
  }
}

export function clearStoredConnection(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Connection history — survives force-disconnects so users can reconnect easily
// ---------------------------------------------------------------------------

export function getConnectionHistory(): ConnectionHistoryEntry[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY)
    if (!raw) return []
    return JSON.parse(raw) as ConnectionHistoryEntry[]
  } catch {
    return []
  }
}

export function addConnectionToHistory(conn: { host: string; port: number; protocol: 'ws' | 'wss' }): void {
  try {
    const history = getConnectionHistory()
    const key = `${conn.protocol}://${conn.host}:${conn.port}`

    // Remove existing entry for the same server (will re-add at top)
    const filtered = history.filter(
      (e) => `${e.protocol}://${e.host}:${e.port}` !== key,
    )

    filtered.unshift({
      host: conn.host,
      port: conn.port,
      protocol: conn.protocol,
      label: key,
      lastConnected: Date.now(),
    })

    // Cap to MAX_HISTORY entries
    localStorage.setItem(HISTORY_KEY, JSON.stringify(filtered.slice(0, MAX_HISTORY)))
  } catch {
    // Safari private browsing may fail
  }
}

export function removeConnectionFromHistory(entry: ConnectionHistoryEntry): void {
  try {
    const history = getConnectionHistory()
    const key = `${entry.protocol}://${entry.host}:${entry.port}`
    const filtered = history.filter(
      (e) => `${e.protocol}://${e.host}:${e.port}` !== key,
    )
    localStorage.setItem(HISTORY_KEY, JSON.stringify(filtered))
  } catch {
    // ignore
  }
}

function isValidHostname(host: string): boolean {
  if (host === 'localhost') return true
  if (/^\d+\.\d+\.\d+\.\d+$/.test(host)) return true
  return host.includes('.')
}

export function parseWsUrl(url: string): { host: string; port: number; protocol: 'ws' | 'wss' } | null {
  try {
    // Handle ws://host:port or wss://host:port format
    const match = url.match(/^(wss?):\/\/([^:/]+):(\d+)/)
    if (match) {
      const host = match[2]
      if (!isValidHostname(host)) return null
      return { protocol: match[1] as 'ws' | 'wss', host, port: parseInt(match[3], 10) }
    }
    // Try parsing as URL
    const parsed = new URL(url)
    if (!isValidHostname(parsed.hostname)) return null
    const port = parsed.port ? parseInt(parsed.port, 10) : 9100
    const protocol = parsed.protocol === 'wss:' ? 'wss' : 'ws'
    return { host: parsed.hostname, port, protocol }
  } catch {
    return null
  }
}

export function connect(host: string, port: number, workspaceId?: string, protocol: 'ws' | 'wss' = 'ws'): WsRpcClient {
  // Destroy previous client if exists
  if (client) {
    client.destroy()
    client = null
  }

  const url = `${protocol}://${host}:${port}`
  const opts: WsRpcClientOptions = {
    workspaceId,
    autoReconnect: true,
    mode: 'remote',
  }

  client = new WsRpcClient(url, opts)
  client.connect()
  currentConnection = { host, port, workspaceId, protocol }

  // Set up visibility-based reconnection
  // Safari closes WebSockets when the page is backgrounded
  if (visibilityHandler) {
    document.removeEventListener('visibilitychange', visibilityHandler)
  }
  visibilityHandler = () => {
    if (document.visibilityState === 'visible' && client && !client.isConnected) {
      // Don't reconnect if we were kicked by the host
      const state = client.getConnectionState()
      if (state.lastClose?.code === 4008) return
      client.reconnectNow()
    }
  }
  document.addEventListener('visibilitychange', visibilityHandler)

  return client
}

export function getClient(): WsRpcClient | null {
  return client
}

export function getCurrentConnection(): StoredConnection | null {
  return currentConnection
}

/**
 * Update the workspace ID on the active client.
 * This ensures reconnections send the correct workspace in the handshake,
 * so the server routes push events correctly after a reconnect.
 */
export function setClientWorkspaceId(workspaceId: string): void {
  client?.setWorkspaceId(workspaceId)
}

export function disconnect(): void {
  if (visibilityHandler) {
    document.removeEventListener('visibilitychange', visibilityHandler)
    visibilityHandler = null
  }
  if (client) {
    client.destroy()
    client = null
  }
  currentConnection = null
}

export function setupElectronApi(rpcClient: WsRpcClient): void {
  const api = buildClientApi(
    rpcClient,
    CHANNEL_MAP,
    (channel: string) => rpcClient.isChannelAvailable(channel),
  )

  // Apply web stubs for Electron-specific methods
  applyWebStubs(api, rpcClient)

  // Assign to window.electronAPI so all existing components work
  ;(window as any).electronAPI = api
}

function applyWebStubs(api: Record<string, any>, rpcClient: WsRpcClient): void {
  // Shell operations — web equivalents
  api.openUrl = async (url: string) => { window.open(url, '_blank', 'noopener,noreferrer') }
  api.openFile = async () => { /* no-op on remote client */ }
  api.showInFolder = async () => { /* no-op on remote client */ }

  // Window management — no-ops
  api.closeWindow = async () => {}
  api.confirmCloseWindow = async () => {}
  api.cancelCloseWindow = async () => {}
  api.onCloseRequested = () => () => {}
  api.setTrafficLightsVisible = async () => {}
  api.openSessionInNewWindow = async () => {}

  // Menu actions — no-ops
  api.menuQuit = async () => {}
  api.menuNewWindow = async () => {}
  api.menuMinimize = async () => {}
  api.menuMaximize = async () => {}
  api.menuZoomIn = async () => {}
  api.menuZoomOut = async () => {}
  api.menuZoomReset = async () => {}
  api.menuToggleDevTools = async () => {}
  api.menuUndo = async () => { document.execCommand('undo') }
  api.menuRedo = async () => { document.execCommand('redo') }
  api.menuCut = async () => { document.execCommand('cut') }
  api.menuCopy = async () => { document.execCommand('copy') }
  api.menuPaste = async () => { document.execCommand('paste') }
  api.menuSelectAll = async () => { document.execCommand('selectAll') }

  // Menu event listeners — no-ops (return cleanup functions)
  api.onMenuNewChat = () => () => {}
  api.onMenuOpenSettings = () => () => {}
  api.onMenuKeyboardShortcuts = () => () => {}
  api.onMenuToggleFocusMode = () => () => {}
  api.onMenuToggleSidebar = () => () => {}

  // Deep link — no-op
  api.onDeepLinkNavigate = () => () => {}

  // System — web equivalents
  api.getVersions = () => ({ node: 'N/A', chrome: navigator.userAgent, electron: 'N/A' })

  // Auth dialogs — use browser confirm
  api.showLogoutConfirmation = async () => window.confirm('Are you sure you want to log out?')
  api.showDeleteSessionConfirmation = async (name: string) => window.confirm(`Delete session "${name}"?`)

  // OAuth — web/popup-based stubs
  api.performOAuth = async (args: { sourceSlug: string; provider?: string; callbackPort?: number; sessionId?: string; authRequestId?: string }) => {
    try {
      const result = await rpcClient.invoke('oauth:start', {
        sourceSlug: args.sourceSlug,
        sessionId: args.sessionId,
        authRequestId: args.authRequestId,
        callbackPort: args.callbackPort ?? 0, // No local callback server on remote client
      })
      if (result?.authUrl) {
        window.open(result.authUrl, '_blank', 'noopener,noreferrer')
      }
      return { success: false, error: 'Complete the OAuth flow in the browser window, then return here.' }
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'OAuth flow failed' }
    }
  }

  api.startClaudeOAuth = async () => {
    try {
      const result = await rpcClient.invoke('onboarding:startClaudeOAuth')
      if (result?.authUrl) {
        window.open(result.authUrl, '_blank', 'noopener,noreferrer')
      }
      return result ?? { success: false, error: 'No response from server' }
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Failed to start OAuth' }
    }
  }

  api.startChatGptOAuth = async (_connectionSlug: string) => {
    return { success: false, error: 'ChatGPT OAuth requires the desktop app. Please configure on your desktop.' }
  }

  // Auto-update — no-ops (desktop only)
  api.checkForUpdates = async () => ({ available: false })
  api.getUpdateInfo = async () => ({ available: false })
  api.installUpdate = async () => {}
  api.dismissUpdate = async () => {}
  api.getDismissedUpdateVersion = async () => null
  api.onUpdateAvailable = () => () => {}
  api.onUpdateDownloadProgress = () => () => {}

  // Release notes — return empty
  api.getReleaseNotes = async () => ''
  api.getLatestReleaseVersion = async () => undefined

  // Badge — no-ops (desktop dock badge)
  api.refreshBadge = async () => {}
  api.setDockIconWithBadge = async () => {}
  api.onBadgeDraw = () => () => {}
  api.onBadgeDrawWindows = () => () => {}

  // Window focus — always focused on iPad
  api.getWindowFocusState = async () => true
  api.onWindowFocusChange = (cb: (focused: boolean) => void) => {
    const handler = () => cb(document.visibilityState === 'visible')
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }

  // Notifications — use browser Notification API when available, in-app toast as fallback
  api.showNotification = async (title: string, body?: string, _workspaceId?: string, sessionId?: string) => {
    // Try browser notifications (only works in installed PWA on iPadOS)
    if ('Notification' in window) {
      if (Notification.permission === 'default') {
        await Notification.requestPermission().catch(() => {})
      }
      if (Notification.permission === 'granted') {
        const n = new Notification(title, { body })
        if (sessionId) {
          n.onclick = () => {
            window.focus()
            window.dispatchEvent(new CustomEvent('craft:notification-navigate', {
              detail: { sessionId },
            }))
          }
        }
        return
      }
    }
  }

  // Power settings — no-ops
  api.getKeepAwakeWhileRunning = async () => false
  api.setKeepAwakeWhileRunning = async () => {}

  // Git bash — not applicable
  api.checkGitBash = async () => ({ available: false })
  api.browseForGitBash = async () => ({})
  api.setGitBashPath = async () => {}

  // Figma bridge — not applicable
  api.installFigmaDesktopBridge = async () => ({ destinationPath: '' })

  // Folder dialog — no-op (can't access iPad filesystem)
  api.openFolderDialog = async () => null
  api.openFileDialog = async () => []

  // Remote access — we ARE the remote client, these are no-ops
  api.enableRemoteAccess = async () => ({})
  api.disableRemoteAccess = async () => {}
  api.disconnectRemoteClients = async () => ({})
  api.getRemoteAccessState = async () => ({ enabled: false, port: 0, token: '', connections: [] })
  api.onRemoteAccessStateChanged = () => () => {}

  // Transport connection state — wire to WsRpcClient
  api.getTransportConnectionState = async () => rpcClient.getConnectionState()
  api.onTransportConnectionStateChanged = (cb: (state: TransportConnectionState) => void) => {
    return rpcClient.onConnectionStateChanged(cb)
  }
  api.reconnectTransport = async () => rpcClient.reconnectNow()

  // Debug logging — console
  api.debugLog = (...args: unknown[]) => console.log('[debug]', ...args)
}
